import React, { useEffect, useState } from "react";
import { FileText, Image as ImageIcon, File as FileIcon, Download, Eye, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { portalApi } from "../../context/PortalAuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }); }
  catch { return iso; }
};

const fmtSize = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
};

const iconFor = (ft) => (ft === "pdf" ? FileText : ft === "image" ? ImageIcon : FileIcon);

const iconTone = (ft) =>
  ft === "pdf" ? "bg-rose-50 text-rose-600 border-rose-200"
  : ft === "image" ? "bg-sky-50 text-sky-600 border-sky-200"
  : "bg-slate-50 text-slate-600 border-slate-200";

/** Fetch a portal document as a blob URL (uses Bearer token via portalApi). */
async function fetchBlobUrl(docId) {
  const res = await portalApi.get(`/portal/documents/${docId}/file`, { responseType: "blob" });
  return URL.createObjectURL(res.data);
}

/** Trigger a browser download for a portal document (attachment stream). */
async function downloadDocument(doc) {
  try {
    const res = await portalApi.get(`/portal/documents/${doc.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = doc.name || `${doc.id}.bin`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    toast.success("Download iniciado");
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Não foi possível baixar o documento.");
  }
}

/** Card rendering a single portal document with view/download actions. */
export function PortalDocumentCard({ doc, onView }) {
  const Icon = iconFor(doc.file_type);
  return (
    <div
      data-testid={`portal-doc-card-${doc.id}`}
      className="rounded-2xl border border-border bg-white p-4 flex items-start gap-3 transition hover:border-primary/40 hover:shadow-sm"
    >
      <div className={`h-11 w-11 shrink-0 rounded-lg border grid place-items-center ${iconTone(doc.file_type)}`}>
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold text-foreground truncate" title={doc.name}>
            {doc.name}
          </p>
          <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
            {doc.category_label || doc.category}
          </Badge>
        </div>
        <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
          {doc.service_name || "—"}
          {doc.receipt_number ? ` · ${doc.receipt_number}` : ""}
        </p>
        <div className="flex items-center gap-2 mt-1 text-[11px] text-muted-foreground">
          <span>{fmtDate(doc.created_at || doc.payment_date)}</span>
          {doc.size ? <span>· {fmtSize(doc.size)}</span> : null}
          <span className="uppercase font-mono text-[10px] text-muted-foreground/80">{doc.file_type}</span>
        </div>
      </div>
      <div className="flex flex-col md:flex-row gap-2 shrink-0">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onView(doc)}
          data-testid={`portal-doc-view-${doc.id}`}
        >
          <Eye className="h-4 w-4 mr-1" /> Visualizar
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => downloadDocument(doc)}
          data-testid={`portal-doc-download-${doc.id}`}
        >
          <Download className="h-4 w-4 mr-1" /> Baixar
        </Button>
      </div>
    </div>
  );
}

/** Modal viewer supporting PDF embed and image preview, with "Open in new tab" + Download. */
export function PortalDocumentViewer({ doc, open, onClose }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !doc) return;
    let alive = true;
    let currentUrl = "";
    setLoading(true); setError(""); setBlobUrl("");
    fetchBlobUrl(doc.id)
      .then((url) => { if (!alive) { URL.revokeObjectURL(url); return; } currentUrl = url; setBlobUrl(url); })
      .catch((e) => alive && setError(e?.response?.data?.detail || "Não foi possível carregar o documento."))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [open, doc]);

  const openInNewTab = () => {
    if (!blobUrl) return;
    const w = window.open(blobUrl, "_blank", "noopener,noreferrer");
    if (!w) toast.error("O navegador bloqueou a nova aba. Permita popups para este site.");
  };

  if (!doc) return null;
  const isPdf = doc.file_type === "pdf";
  const isImg = doc.file_type === "image";

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-5xl w-[95vw] p-0 overflow-hidden"
        data-testid="portal-doc-viewer"
      >
        <DialogHeader className="px-5 py-3 border-b border-border">
          <DialogTitle className="text-base font-semibold truncate pr-8" title={doc.name}>
            {doc.name}
          </DialogTitle>
          <DialogDescription className="sr-only">
            Pré-visualização do documento {doc.name}. Use o botão Baixar para salvar uma cópia.
          </DialogDescription>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="text-[10px]">{doc.category_label || doc.category}</Badge>
            <span className="text-[11px] text-muted-foreground">{fmtDate(doc.created_at)}</span>
            {doc.size ? <span className="text-[11px] text-muted-foreground">· {fmtSize(doc.size)}</span> : null}
          </div>
        </DialogHeader>

        <div className="bg-muted/30 h-[70vh] relative">
          {loading && (
            <div className="absolute inset-0 grid place-items-center" data-testid="portal-doc-viewer-loading">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Carregando documento…
              </div>
            </div>
          )}
          {!loading && error && (
            <div className="absolute inset-0 grid place-items-center px-6 text-center" data-testid="portal-doc-viewer-error">
              <p className="text-sm text-rose-600">{error}</p>
            </div>
          )}
          {!loading && !error && blobUrl && isPdf && (
            <iframe
              title={doc.name}
              src={blobUrl}
              className="w-full h-full bg-white"
              data-testid="portal-doc-viewer-pdf-frame"
            />
          )}
          {!loading && !error && blobUrl && isImg && (
            <div className="w-full h-full overflow-auto flex items-center justify-center p-4">
              <img
                src={blobUrl}
                alt={doc.name}
                className="max-w-full max-h-full object-contain shadow-sm rounded"
                data-testid="portal-doc-viewer-image"
              />
            </div>
          )}
          {!loading && !error && blobUrl && !isPdf && !isImg && (
            <div className="absolute inset-0 grid place-items-center px-6 text-center" data-testid="portal-doc-viewer-fallback">
              <div>
                <FileIcon className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-3">
                  Este tipo de arquivo não pode ser pré-visualizado no navegador.
                </p>
                <Button onClick={() => downloadDocument(doc)}>
                  <Download className="h-4 w-4 mr-1" /> Baixar arquivo
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border bg-white">
          <Button
            variant="outline"
            size="sm"
            onClick={openInNewTab}
            disabled={!blobUrl || !!error}
            data-testid="portal-doc-viewer-newtab"
          >
            <ExternalLink className="h-4 w-4 mr-1" /> Abrir em nova aba
          </Button>
          <Button
            size="sm"
            onClick={() => downloadDocument(doc)}
            data-testid="portal-doc-viewer-download"
          >
            <Download className="h-4 w-4 mr-1" /> Baixar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { downloadDocument };
